import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  ForgeButton,
  ForgeDialog,
  ForgeScaffold,
  ForgeStack,
  ForgeTextField,
  ForgeToolbar
} from '@tylertech/forge-react';
import { enrollMfaFactor, activateMfaFactor, removeMfaFactor, getMfaFactors } from '../../api/mfa';
import { MFA_FACTORS_QUERY_KEY } from '../../hooks/useMfaFactors';
import useProfile from '../../hooks/useProfile';

const PHONE_STAGE = 0;
const VERIFY_STAGE = 1;

const normalizePhone = (phone) => {
  if (!phone) return '';
  const digits = phone.replace(/[^\d]/g, '');
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  return phone.startsWith('+') ? `+${digits}` : digits;
};

const formatPhoneDisplay = (phone) => {
  if (!phone) return '';
  const digits = phone.replace(/[^\d]/g, '');
  const national = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (national.length === 10) {
    return `(${national.slice(0, 3)}) ${national.slice(3, 6)}-${national.slice(6)}`;
  }
  return phone;
};

export default function EnableSmsDialog({ open, onClose, onComplete, existingFactorId }) {
  const queryClient = useQueryClient();
  const { profile } = useProfile();

  const [stage, setStage] = useState(PHONE_STAGE);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [factorId, setFactorId] = useState(null);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const profilePhone = profile?.phone || '';

  const enrollWithConflictRecovery = async (normalized) => {
    try {
      return await enrollMfaFactor({ factorType: 'sms', phoneNumber: normalized });
    } catch (err) {
      const status = err?.response?.status;
      if (status !== 409 && status !== 500) throw err;
      const { factors = [] } = (await getMfaFactors()) ?? {};
      const stale = factors.find(f => f.type === 'sms');
      const staleId = stale?.id || stale?.factorId;
      if (!staleId) throw err;
      await removeMfaFactor(staleId);
      return enrollMfaFactor({ factorType: 'sms', phoneNumber: normalized });
    }
  };

  const resetState = () => {
    setStage(PHONE_STAGE);
    setPhoneNumber('');
    setVerificationCode('');
    setFactorId(null);
    setError('');
    setIsSubmitting(false);
  };

  const handleClose = () => {
    if (isSubmitting) return;
    resetState();
    onClose();
  };

  const handlePhoneInput = (event) => {
    const value = event.target.value.replace(/[^\d()\-\s+]/g, '');
    setPhoneNumber(value);
    setError('');
  };

  const handleUseProfilePhone = () => {
    setPhoneNumber(formatPhoneDisplay(profilePhone));
    setError('');
  };

  const handleSendCode = async (event) => {
    event?.preventDefault?.();
    const normalized = normalizePhone(phoneNumber);
    const digits = normalized.replace(/[^\d]/g, '');

    if (digits.length !== 11 || !digits.startsWith('1')) {
      setError('Enter a valid 10-digit US phone number.');
      return;
    }

    setError('');
    setIsSubmitting(true);
    try {
      if (existingFactorId) {
        try {
          await removeMfaFactor(existingFactorId);
        } catch (removeErr) {
          if (removeErr?.response?.status !== 404) throw removeErr;
        }
        await queryClient.invalidateQueries({ queryKey: MFA_FACTORS_QUERY_KEY });
      }
      const result = await enrollWithConflictRecovery(normalized);
      if (!result?.factorId) {
        setError('Unable to start verification. Please try again.');
        return;
      }

      setFactorId(result.factorId);
      setStage(VERIFY_STAGE);
    } catch (err) {
      const message = err?.response?.data?.message || 'Unable to send verification code. Please try again.';
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerify = async (event) => {
    event?.preventDefault?.();

    if (!factorId) {
      setError('Verification session expired. Please start over.');
      setStage(PHONE_STAGE);
      return;
    }

    const code = verificationCode.trim();

    if (!/^[0-9]{6}$/.test(code)) {
      setError('Code must be 6 digits.');
      return;
    }

    setError('');
    setIsSubmitting(true);
    try {
      await activateMfaFactor(factorId, code);
      await queryClient.invalidateQueries({ queryKey: MFA_FACTORS_QUERY_KEY });
      if (typeof onComplete === 'function') {
        onComplete();
      }
      resetState();
      onClose();
    } catch (err) {
      const message = err?.response?.data?.message || 'Verification failed. Check the code and try again.';
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const canSendCode = phoneNumber.replace(/[^\d]/g, '').length >= 10 && !isSubmitting;
  const canVerify = verificationCode.length === 6 && !isSubmitting;

  return (
    <ForgeDialog
      className="dialog--large"
      open={open}
      on-forge-dialog-close={handleClose}
      label="Enable SMS Authentication"
      description="Set up SMS text message verification for your account.">
      <ForgeScaffold>
        <ForgeToolbar slot="header" className="dialog-header">
          <h2 slot="start" className="forge-typography--subheading6">
            {stage === PHONE_STAGE ? 'Enable SMS Authentication' : 'Verify Your Phone Number'}
          </h2>
        </ForgeToolbar>

        {stage === PHONE_STAGE ? (
          <form slot="body" className="dialog-body" onSubmit={handleSendCode}>
            <ForgeStack gap="16">
              {error ? <p className="dialog-status dialog-status--error">{error}</p> : null}
              <p className="forge-typography--body1">
                Enter your phone number to receive a verification code via text message.
                This number will be used for multi-factor authentication and is stored separately from your profile phone number.
              </p>
              <ForgeTextField>
                <label slot="label">Phone number</label>
                <input
                  type="tel"
                  value={phoneNumber}
                  aria-label="Phone number for SMS verification"
                  autoFocus
                  placeholder="(555) 123-4567"
                  onInput={handlePhoneInput}
                  disabled={isSubmitting}
                  required
                />
              </ForgeTextField>
              {profilePhone ? (
                <ForgeButton variant="text" type="button" onClick={handleUseProfilePhone} disabled={isSubmitting}>
                  Use my profile number
                </ForgeButton>
              ) : null}
              <button type="submit" style={{ display: 'none' }} aria-hidden="true"></button>
            </ForgeStack>
          </form>
        ) : (
          <form slot="body" className="dialog-body" onSubmit={handleVerify}>
            <ForgeStack gap="16">
              {error ? <p className="dialog-status dialog-status--error">{error}</p> : null}
              <p className="forge-typography--body1">
                A verification code has been sent to your phone. Enter the 6-digit code below.
              </p>
              <p className="forge-typography--body2 text-color--medium">
                Do not leave this page or the code will not work.
              </p>
              <ForgeTextField>
                <label slot="label">Verification code</label>
                <input
                  type="text"
                  id="sms-verification-code"
                  value={verificationCode}
                  aria-label="6-digit SMS verification code"
                  autoComplete="one-time-code"
                  autoFocus
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  onInput={(event) => setVerificationCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
                  disabled={isSubmitting}
                  required
                />
              </ForgeTextField>
              <button type="submit" style={{ display: 'none' }} aria-hidden="true"></button>
            </ForgeStack>
          </form>
        )}

        <ForgeToolbar slot="footer" inverted>
          <ForgeStack slot="end" inline>
            <ForgeButton variant="outlined" onClick={handleClose} disabled={isSubmitting}>
              Cancel
            </ForgeButton>
            {stage === PHONE_STAGE ? (
              <ForgeButton variant="filled" onClick={handleSendCode} disabled={!canSendCode}>
                {isSubmitting ? 'Sending...' : 'Send Code'}
              </ForgeButton>
            ) : (
              <ForgeButton variant="filled" onClick={handleVerify} disabled={!canVerify}>
                {isSubmitting ? 'Verifying...' : 'Verify'}
              </ForgeButton>
            )}
          </ForgeStack>
        </ForgeToolbar>
      </ForgeScaffold>
    </ForgeDialog>
  );
}
